/**
 * routeEngine.js
 *
 * Given a TriageOutput and user context, queries the Supabase providers
 * table, scores each result, and returns the top N ranked providers.
 *
 * API:
 *   getRankedProviders(triageOutput, userContext, options?) → Promise<RankedProvider[]>
 *
 * RankedProvider shape:
 *   { ...providerRow, score: number }
 *
 * Emergency (emergency_now) shortcut:
 *   Returns the nearest ER providers only. If location is unknown, falls
 *   back to all ER providers sorted by featured then score.
 */

import { supabase } from '../supabase';
import { scoreProvider } from './providerScoring';
import { getState } from '../userStore';

const DEFAULT_TOP_N = 3;
const CANDIDATE_POOL = 40; // fetch this many from DB before scoring

/**
 * Map care sites to provider category values for DB filtering.
 */
const SITE_TO_DB_CATEGORY = {
  er: ['er'],
  urgent_care: ['urgent_care'],
  primary_care: ['primary'],
  pediatrician: ['pediatric'],
  telehealth: ['primary', 'urgent_care'],
  orthopedic_urgent_care: ['orthopedic'],
};

/**
 * Build the category filter list for a given set of suggestedCareSites.
 * @param {string[]} sites
 * @returns {string[]}
 */
function buildCategoryFilter(sites) {
  const cats = new Set();
  for (const site of sites) {
    (SITE_TO_DB_CATEGORY[site] || []).forEach(c => cats.add(c));
  }
  return [...cats];
}

/**
 * Fetch candidate providers from Supabase.
 *
 * @param {string[]} categories   — provider category values to include
 * @param {number} limit
 * @returns {Promise<object[]>}
 */
async function fetchCandidates(categories, limit) {
  let query = supabase
    .from('providers')
    .select(
      'npi, name, practice_name, address, city, state, zip, phone, specialty, category, featured, status, insurance, telehealth, same_day, lat, lng, maps_url, book_url, hours, description, practices, practice_id'
    )
    .neq('status', 'not_accepting')
    .limit(limit);

  if (categories.length > 0) {
    query = query.in('category', categories);
  }

  // Featured providers first in the DB pull
  query = query.order('featured', { ascending: false });

  const { data, error } = await query;
  if (error) {
    console.warn('[routeEngine] Supabase fetch error:', error.message);
    return [];
  }
  return data || [];
}

/**
 * Get ranked providers for a triage result.
 *
 * @param {object} triageOutput  — from triageEngine.evaluate()
 * @param {object} [overrideCtx] — optional context overrides (carrier, ageBand, userLat, userLng)
 * @param {object} [options]     — { topN: number }
 * @returns {Promise<object[]>}  — top providers with `score` appended
 */
export async function getRankedProviders(triageOutput, overrideCtx = {}, options = {}) {
  const topN = options.topN || DEFAULT_TOP_N;
  const { suggestedCareSites = [] } = triageOutput;

  // Build user context from userStore + any overrides
  const store = getState();
  const userContext = {
    carrier: store.carrier || null,
    ageBand: overrideCtx.ageBand || null,
    userLat: overrideCtx.userLat ?? store.userLat ?? null,
    userLng: overrideCtx.userLng ?? store.userLng ?? null,
    ...overrideCtx,
  };

  const categories = buildCategoryFilter(suggestedCareSites);

  // For emergency_now, always fetch ERs regardless of what was passed
  // emergency_now → always ERs; empty sites → fall back based on care level
  const fallbackCategories = triageOutput.careLevel === 'home_care'
    ? ['primary']
    : ['primary', 'urgent_care'];
  const finalCategories =
    triageOutput.careLevel === 'emergency_now'
      ? ['er']
      : categories.length > 0
        ? categories
        : fallbackCategories;

  const candidates = await fetchCandidates(finalCategories, CANDIDATE_POOL);

  if (candidates.length === 0) return [];

  // Score and sort
  const scored = candidates.map(provider => ({
    ...provider,
    score: scoreProvider(provider, triageOutput, userContext),
  }));

  scored.sort((a, b) => {
    // Featured providers get a tiebreaker boost
    if (b.featured !== a.featured) return b.featured ? 1 : -1;
    return b.score - a.score;
  });

  return scored.slice(0, topN);
}

/**
 * Get the nearest ER providers for emergency routing.
 * Always returns up to 3 ERs sorted by distance if location is available,
 * otherwise by featured status and score.
 *
 * @param {{ userLat?: number, userLng?: number }} locationCtx
 * @returns {Promise<object[]>}
 */
export async function getNearestERs(locationCtx = {}) {
  const store = getState();
  const userLat = locationCtx.userLat ?? store.userLat ?? null;
  const userLng = locationCtx.userLng ?? store.userLng ?? null;

  const emergencyOutput = {
    careLevel: 'emergency_now',
    suggestedCareSites: ['er'],
    reasonCodes: [],
  };

  return getRankedProviders(
    emergencyOutput,
    { userLat, userLng },
    { topN: 3 }
  );
}
